import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { MapPin, Mail } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { trackClick } from "@/hooks/useAnalytics";

export const Contact = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, {
    once: true,
    margin: "-100px"
  });

  return (
    <section id="contato" className="py-12 lg:py-16" ref={containerRef}>
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 lg:mb-24 text-center"
        >
          <span className="text-primary text-sm font-medium tracking-widest uppercase mb-4 block">
            Fale Conosco
          </span>
          <h2 className="font-display text-4xl lg:text-6xl xl:text-7xl font-light text-foreground mb-6">
            Contato
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Entre em contato para tirar dúvidas e agendar um atendimento.
          </p>
        </motion.div>

        {/* Contact Info - Grid with equal widths */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 max-w-4xl mx-auto mb-16"
        >
          {/* WhatsApp */}
          <motion.a
            href="https://wa.me/5511975489900"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick("Whatsapp", "Contact Section")}
            whileHover={{ scale: 1.05, y: -5 }}
            className="flex flex-col items-center gap-4 group cursor-pointer"
          >
            <div className="w-16 h-16 rounded-full bg-green-600/10 flex items-center justify-center group-hover:bg-green-600/20 transition-colors">
              <WhatsAppIcon className="w-7 h-7 text-green-500" aria-hidden="true" />
            </div>
            <div className="text-center">
              <h3 className="text-foreground font-medium mb-1">WhatsApp</h3>
              <p className="text-muted-foreground text-sm">Clique para conversar</p>
              <p className="text-muted-foreground text-sm">+55 11 97548-9900</p>
            </div>
          </motion.a>

          {/* Email */}
          <motion.a
            href="mailto:sotsekadvocacia@gmail.com"
            onClick={() => trackClick("Email", "Contact Section")}
            whileHover={{ scale: 1.05, y: -5 }}
            className="flex flex-col items-center gap-4 group cursor-pointer"
          >
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <Mail className="w-7 h-7 text-primary" aria-hidden="true" />
            </div>
            <div className="text-center">
              <h3 className="text-foreground font-medium mb-1">E-mail</h3>
              <p className="text-muted-foreground text-sm">sotsekadvocacia@gmail.com</p>
              <p className="text-muted-foreground text-sm invisible">placeholder</p>
            </div>
          </motion.a>

          {/* Address */}
          <motion.a
            href="https://www.google.com/maps/place/Sotsek+Advocacia+Tribut%C3%A1ria"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick("Address", "Contact Section")}
            whileHover={{ scale: 1.05, y: -5 }}
            className="flex flex-col items-center gap-4 group cursor-pointer"
          >
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <MapPin className="w-7 h-7 text-primary" aria-hidden="true" />
            </div>
            <div className="text-center">
              <h3 className="text-foreground font-medium mb-1">Endereço</h3>
              <p className="text-muted-foreground text-sm">R. Francisco Leitão, 653 - Primeiro Andar</p>
              <p className="text-muted-foreground text-sm">Pinheiros, São Paulo - SP, 05414-020</p>
            </div>
          </motion.a>
        </motion.div>

        {/* Google Maps Embed */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl mx-auto"
        >
          <div className="rounded-lg overflow-hidden border border-border">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3657.248538301742!2d-46.6853912246699!3d-23.559515578801136!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce57cfa53ffffb%3A0x21268ba2b5c251df!2sSotsek%20Advocacia%20Tribut%C3%A1ria!5e0!3m2!1spt-BR!2sbr!4v1769906731571!5m2!1spt-BR!2sbr"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização Sotsek Advocacia Tributária"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
